var m = require('mithril');
var formGroup = require('../../components/formGroup');
var Model = require('../../libs/Model');
var PointModel = require('../../../../../models/data/point');

// Keep the configuration form isolated from the shared Point model. Runtime
// values are updated by subscriptions and command endpoints; sending a stale
// copy back with an ordinary configuration save can overwrite live state.
var point = Object.assign({}, PointModel, Model);
point.fields = Object.assign({}, PointModel.fields);

[
  'value',
  'updated',
  'overrideActive',
  'alarmStatus',
  'alarmError',
  'trippedTime',
  'resetTime',
  'nextResetDelay',
  'lastNotificationSentTimestamp',
  'lastTripIncidentNanoId',
  'lastTripTimestamp',
  'influxId',
  'triggerDelayTimeouts',
  'resetDelayTimeouts',
  'acknowledged',
  'acknowledgedBy',
  'acknowledgedTimestap',
  'statusIndex',
  'deleted'
].forEach(function (field) {
  delete point.fields[field];
});

// `parent` is required when a Point is created, while the calibration values
// are maintained by the calibration workflow. None of them belongs in an
// ordinary update of an existing Point.
point.configurationFields = Object.assign({}, point.fields);
delete point.configurationFields.parent;
delete point.configurationFields.calibrationOffset;
delete point.configurationFields.calibrationLastDate;

point.writeConfigFingerprint = function (record) {
  var value = record && record.writeConfigFingerprint;
  return typeof value === 'string' && /^[a-f0-9]{64}$/.test(value) ? value : null;
};

point.requireWriteConfigFingerprint = function (record) {
  var fingerprint = point.writeConfigFingerprint(record);
  if (!fingerprint) {
    var error = new Error('Point write configuration is unavailable. Reload the Point and try again.');
    error.code = 'POINT_WRITE_CONFIG_FINGERPRINT_REQUIRED';
    throw error;
  }
  return fingerprint;
};

point.expectedPointConfiguration = function (records) {
  var expected = {};
  (Array.isArray(records) ? records : [records]).forEach(function (record) {
    var rid = record && record['@rid'];
    if (typeof rid !== 'string' || !/^#[0-9]+:[0-9]+$/.test(rid)) {
      var ridError = new Error('Point identity is unavailable. Reload the Point and try again.');
      ridError.code = 'POINT_WRITE_CONFIG_FINGERPRINT_REQUIRED';
      throw ridError;
    }
    var fingerprint = point.requireWriteConfigFingerprint(record);
    if (expected[rid] && expected[rid] !== fingerprint) {
      var conflict = new Error('Point write configuration is inconsistent. Reload and try again.');
      conflict.code = 'POINT_WRITE_CONFIG_FINGERPRINT_CONFLICT';
      throw conflict;
    }
    expected[rid] = fingerprint;
  });
  return expected;
};

function serializedFormValue(field) {
  var value = point.form && point.form[field];
  if (value && typeof value.toJSON === 'function') {
    return value.toJSON();
  }
  return typeof value === 'function' ? value() : value;
}

function valuesMatch(left, right) {
  if (left === right) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (error) {
    return false;
  }
}

point.changedConfigurationFields = function () {
  if (!point.record || !point.record['@rid'] || !point.form) {
    return point.configurationFields;
  }

  return Object.keys(point.configurationFields).reduce(function (changed, field) {
    if (!valuesMatch(serializedFormValue(field), point.record[field])) {
      changed[field] = point.configurationFields[field];
    }
    return changed;
  }, {});
};

point.commitConfigurationSnapshot = function (fields) {
  if (!point.record || !point.record['@rid']) return;
  Object.keys(fields).forEach(function (field) {
    point.record[field] = serializedFormValue(field);
  });
};

point.save = function () {
  var allFormFields = point.fields;
  var fieldsToSave = point.fields;
  if (point.record && point.record['@rid']) {
    fieldsToSave = point.changedConfigurationFields();
    if (Object.keys(fieldsToSave).length === 0) {
      return Promise.resolve(point.record);
    }
    point.fields = fieldsToSave;
  }

  var savePromise;
  try {
    // Model.saveJSON captures the payload and validators synchronously before
    // returning its request promise, so the complete form can be restored for
    // rendering immediately afterward.
    savePromise = Model.save.call(point);
  } finally {
    point.fields = allFormFields;
  }

  return Promise.resolve(savePromise).then(function (result) {
    if (point.record && point.record['@rid']) {
      point.commitConfigurationSnapshot(fieldsToSave);
    }
    return result;
  });
};

point.initForm();

// View is only used as a dialogue in resource pages for the time being
point.view = function (ctrl) {
  return m('form', {role:'form'}, [
    point.errorView(),
    m(formGroup, [
      m('label', {for:'name-input'}, 'Name'),
      m('input.form-control', {
        id:'name-input', 
        type:'text', 
        oninput: function(ev){point.form.name(ev.target.value)},
        value: point.form.name(),
      })
    ]),
    m(formGroup, [
      m('label', {for:'index-input'}, 'Index'),
      m('input.form-control', {
        id:'index-input', 
        autocomplete: 'off',
        type:'text', 
        oninput: function(ev){point.form.index(ev.target.value)},
        value: point.form.index(),
      })
    ]),
    m(formGroup, [
      m('label', {for:'jsonPath-input'}, 'JSONPath'),
      m('input.form-control', {
        id:'jsonPath-input', 
        autocomplete: 'off',
        type:'text', 
        oninput: function(ev){point.form.jsonPath(ev.target.value)},
        value: point.form.jsonPath(),
      })
    ]),
    m(formGroup, [
      m('label', {for:'jsonPath-input'}, 'Query Selector'),
      m('input.form-control', {
        id:'querySelector-input', 
        autocomplete: 'off',
        type:'text', 
        oninput: function(ev){point.form.querySelector(ev.target.value)},
        value: point.form.querySelector(),
      })
    ]),
    m(formGroup, [
      m('label', {for:'jsonPath-input'}, 'Regular Expression'),
      m('input.form-control', {
        id:'regularExpression-input',
        autocomplete: 'off',
        type:'text', 
        oninput: function(ev){point.form.regex(ev.target.value)},
        value: point.form.regex(),
      })
    ]),
  ])  
}


module.exports = point;
